import { useCallback, useContext } from "react";
import { MenuButton } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { EnumSmartFeedActivity, useFeedActivityMutation } from "src/api/client/feed-activity.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { useLikeActivityMutation } from "src/api/client/like.api";
import { Span } from "src/components/base";
import { ArrowLink } from "src/components/base/arrow-link";
import { Button } from "src/components/base/button";
import { EnumLikeableEntityType } from "src/components/common/buttons/like-button/like-button.types";
import { ShareButton } from "src/components/common/buttons/share/share-button";
import { TShareOption, useShareLinksForComment } from "src/components/common/buttons/share/share-button-util";
import { FeedCardCommentButton } from "src/components/feed-card/common/feed-card-comment-button";
import { FeedCardLayout } from "src/components/feed-card/common/feed-card-layout";
import { FeedCardLikeButton } from "src/components/feed-card/common/feed-card-like-button";
import { FeedCardContext } from "src/components/feed-card/feed-card";
import { IconShare } from "src/components/icons";
import { EnumFeedClickTrackingArea, useFeedClickTracking } from "src/components/pages/feed/hooks/use-feed-click-tracking";
import { triggerCallbackOnMiddleClickEvent } from "src/utils/dom/mouse-util";
import { feedTrackingStore } from "./store/feed-tracking-store";
import { IFeedCardFooter, IFeedCardUserReactionContentReference } from "./types/feed-card";
import { isUrlPathnameSameAsCurrentPage } from "./utils/link-helper";

interface IFeedCardFooterProps extends IFeedCardFooter {
    //TODO: Remove the "?" when userActivityEntity will get replaced completely by userReactionContentReferences in the Feed API
    userReactionContentReferences?: IFeedCardUserReactionContentReference[] | null;
}

export const FeedCardFooter = ({ likeInfo, shareInfo, commentInfo, link, userReactionContentReferences }: IFeedCardFooterProps) => {
    const shareUrl = shareInfo?.url;
    const feedActivityMutatePromise = useFeedActivityMutation();
    const context = useContext(FeedCardContext);
    const { t } = useTranslation("common");
    const shareOptions = useShareLinksForComment(shareUrl ?? "");
    const { trackFeedClick } = useFeedClickTracking();
    const likeActivity = useLikeActivityMutation();

    const trackFeedItemLiked = useCallback(() => {
        feedActivityMutatePromise({
            itemId: context.contentId,
            type: EnumSmartFeedActivity.SmartFeedItemLiked,
            parameter: JSON.stringify({
                feedType: feedTrackingStore.feedType,
                page: feedTrackingStore.page,
                wikifolioId: feedTrackingStore.wikifolioId,
            }),
        });

        const commentEntity = userReactionContentReferences?.find(entity => entity.userReactionContentType === EnumLikeableEntityType.Comment);
        if (commentEntity) {
            likeActivity({ parameter: commentEntity.userReactionContentId, activityType: EnumActivityType.LikedTraderComment });
        }

        const magazineEntity = userReactionContentReferences?.find(entity => entity.userReactionContentType === EnumLikeableEntityType.Magazine);
        if (magazineEntity) {
            return likeActivity({ parameter: magazineEntity.userReactionContentId, activityType: EnumActivityType.MagazineArticleStarred });
        }
    }, [context.contentId, feedActivityMutatePromise, likeActivity, userReactionContentReferences]);

    const trackFeedItemShared = useCallback(
        (shareMedium: TShareOption) =>
            feedActivityMutatePromise({
                itemId: context.contentId,
                type: EnumSmartFeedActivity.SmartFeedItemShared,
                parameter: JSON.stringify({
                    shareMedium,
                    feedType: feedTrackingStore.feedType,
                    page: feedTrackingStore.page,
                    wikifolioId: feedTrackingStore.wikifolioId,
                }),
            }),
        [context.contentId, feedActivityMutatePromise]
    );

    const handleLinkClick = useCallback(
        () => link && trackFeedClick(context.contentId, link.url, EnumFeedClickTrackingArea.Footer),
        [context.contentId, link, trackFeedClick]
    );

    const handleCommentButtonClick = useCallback(
        () => commentInfo && trackFeedClick(context.contentId, commentInfo.url, EnumFeedClickTrackingArea.Footer),
        [commentInfo, trackFeedClick, context.contentId]
    );

    return (
        <FeedCardLayout.Footer>
            {likeInfo && userReactionContentReferences && (
                <FeedCardLikeButton
                    likeInfo={likeInfo}
                    entityReferences={userReactionContentReferences.map(entity => ({
                        entityId: entity.userReactionContentId,
                        entityType: entity.userReactionContentType,
                    }))}
                    onClick={trackFeedItemLiked}
                />
            )}
            {commentInfo && <FeedCardCommentButton onClick={handleCommentButtonClick} url={commentInfo.url} count={commentInfo.count} />}
            {shareInfo && shareUrl && (
                <ShareButton
                    link={shareUrl}
                    shareOptions={shareOptions}
                    webShareData={{ url: shareUrl }}
                    onMiddleClick={trackFeedItemShared}
                    onClick={trackFeedItemShared}
                >
                    <MenuButton as={Button} size="sm" variant="ghost" px={2} leftIcon={<IconShare boxSize={3} />}>
                        <Span display={["none", "initial", "none", "initial"]}>{t("share")}</Span>
                    </MenuButton>
                </ShareButton>
            )}
            {link && !isUrlPathnameSameAsCurrentPage(link.url) && (
                <ArrowLink
                    size="sm"
                    ml="auto"
                    href={link.url}
                    target="_blank"
                    onClick={handleLinkClick}
                    onAuxClick={triggerCallbackOnMiddleClickEvent(handleLinkClick)}
                >
                    {link.name}
                </ArrowLink>
            )}
        </FeedCardLayout.Footer>
    );
};
